import { Link } from 'react-router-dom';
import { categories, articles, faqs, testimonials } from '@/mocks/mahjong';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function HomePage() {
  const featuredCategories = categories.slice(0, 4);
  const featuredArticles = articles.slice(0, 3);

  const homeSchema = {
    Organization: {
      name: '在线麻将牌',
      url: 'https://tileblast.github.io',
      description: '免费在线麻将游戏平台，支持多种麻将玩法，随时随地享受正宗麻将乐趣。',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+86-400-000-0000',
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
      },
    },
    WebSite: {
      name: '在线麻将牌',
      url: 'https://tileblast.github.io',
      description: '免费在线麻将游戏平台，支持多种麻将玩法，随时随地享受正宗麻将乐趣。',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://tileblast.github.io/search?q={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
    WebPage: {
      name: '在线麻将牌 - 免费畅玩正宗麻将游戏',
      description: '在线麻将牌是随时随地都能享受的经典棋牌娱乐，无需下载即可免费畅玩。支持多种麻将玩法，画面清晰、操作流畅，与全国玩家实时对战。',
      url: 'https://tileblast.github.io',
      isPartOf: {
        '@type': 'WebSite',
        name: '在线麻将牌',
      },
    },
    FAQPage: {
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  };

  return (
    <>
      <SchemaOrg type="Organization" data={homeSchema.Organization} />
      <SchemaOrg type="WebSite" data={homeSchema.WebSite} />
      <SchemaOrg type="WebPage" data={homeSchema.WebPage} />
      <SchemaOrg type="FAQPage" data={homeSchema.FAQPage} />

      {/* Hero Section */}
      <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20dark%20green%20mahjong%20table%20felt%20texture%20with%20scattered%20traditional%20Chinese%20mahjong%20tiles%2C%20warm%20golden%20ambient%20lighting%2C%20soft%20bokeh%20background%2C%20luxurious%20gaming%20atmosphere%2C%20deep%20rich%20colors%2C%20professional%20product%20photography%2C%20abstract%20artistic%20composition&width=1920&height=1080&seq=hero-bg-main&orientation=landscape"
          alt="在线麻将牌 - 免费畅玩正宗麻将游戏"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4">
          <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 max-w-3xl leading-tight">
            随时随地，畅享正宗<strong className="text-accent-400">在线麻将牌</strong>乐趣
          </h1>
          <p className="text-base md:text-lg text-white/80 max-w-2xl mb-8 leading-relaxed">
            无需下载，免费畅玩。支持国标、广东、四川等多种麻将玩法，与全国玩家实时对战，体验最正宗的在线麻将牌！
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <Link
              to="/category/guobiao"
              className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-base font-semibold hover:bg-primary-600 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              立即开始游戏
            </Link>
            <Link
              to="/articles"
              className="px-8 py-3 bg-white/15 backdrop-blur-sm text-white rounded-full text-base font-medium hover:bg-white/25 transition-all whitespace-nowrap cursor-pointer"
            >
              新手攻略指南
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              为什么选择<strong>在线麻将牌</strong>？
            </h2>
            <p className="text-foreground-600 max-w-2xl mx-auto">
              我们致力于打造最好的免费在线麻将平台，让每一位玩家都能享受最纯粹的游戏乐趣
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                icon: 'ri-smartphone-line',
                title: '无需下载',
                desc: '打开浏览器即刻开始游戏，支持手机、平板、电脑全平台',
              },
              {
                icon: 'ri-group-line',
                title: '实时对战',
                desc: '智能匹配全国玩家，秒速开局，告别三缺一的烦恼',
              },
              {
                icon: 'ri-gamepad-line',
                title: '多种玩法',
                desc: '国标、广东、四川等经典玩法一应俱全，任你选择',
              },
              {
                icon: 'ri-shield-check-line',
                title: '公平公正',
                desc: '认证随机算法，系统自动计分，杜绝一切作弊行为',
              },
            ].map((feature, idx) => (
              <div
                key={idx}
                className="bg-background-50 border border-background-200/70 rounded-xl p-6 hover:border-primary-200 transition-all duration-300 group"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                  <i className={`${feature.icon} text-xl`}></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              丰富的<strong>麻将玩法</strong>分类
            </h2>
            <p className="text-foreground-600 max-w-2xl mx-auto">
              从经典国标到地方特色，总有一款适合你的麻将玩法
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop="">
            {featuredCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={cat.image}
                    alt={`${cat.name} - 在线麻将牌`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    title={`${cat.name} - 在线麻将牌`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                  <div className="absolute bottom-3 left-3 flex items-center gap-2">
                    <span className="bg-accent-500 text-background-50 text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                      {cat.difficulty}
                    </span>
                    <span className="bg-black/40 text-white text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                      {cat.playerCount}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    <h3 className="font-heading font-semibold text-foreground-900">
                      {cat.name}
                    </h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    {cat.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/category/guobiao"
              className="inline-flex items-center gap-2 px-6 py-2.5 border border-primary-300 text-primary-700 rounded-full text-sm font-medium hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              查看全部玩法
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              玩家<strong>真实评价</strong>
            </h2>
            <p className="text-foreground-600 max-w-2xl mx-auto">
              听听玩家们怎么说，他们的认可就是我们前进的动力
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((t, idx) => (
              <div
                key={idx}
                className="bg-background-50 border border-background-200/70 rounded-xl p-6"
              >
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <span key={i} className="w-4 h-4 flex items-center justify-center text-accent-500">
                      <i className="ri-star-fill text-sm"></i>
                    </span>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  &ldquo;{t.text}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <span className="text-sm font-medium text-foreground-900">
                    {t.name}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                热门<strong>麻将攻略</strong>
              </h2>
              <p className="text-foreground-600 text-sm">
                从入门到精通，全方位提升你的麻将水平
              </p>
            </div>
            <Link
              to="/articles"
              className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer"
            >
              查看全部文章
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featuredArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    title={article.title}
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-secondary-100 text-secondary-700 text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                      {article.categoryName}
                    </span>
                    <span className="text-xs text-foreground-500 whitespace-nowrap">
                      {article.date}
                    </span>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    {article.excerpt}
                  </p>
                  <div className="mt-3 flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-time-line"></i>
                    <span>{article.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8 sm:hidden">
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 whitespace-nowrap cursor-pointer"
            >
              查看全部文章
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              常见<strong>问题解答</strong>
            </h2>
            <p className="text-foreground-600">
              关于在线麻将牌的常见疑问，这里都有答案
            </p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <details
                key={idx}
                className="group border border-background-200/70 rounded-xl overflow-hidden"
              >
                <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-100 transition-colors list-none">
                  <h3 className="font-heading text-base font-medium text-foreground-900 pr-4">
                    {faq.question}
                  </h3>
                  <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-180 transition-transform">
                    <i className="ri-arrow-down-s-line"></i>
                  </span>
                </summary>
                <div className="px-5 pb-5">
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20cozy%20game%20room%20with%20mahjong%20table%20in%20center%2C%20soft%20golden%20evening%20light%20streaming%20through%20window%2C%20inviting%20Chinese%20interior%20design%2C%20peaceful%20atmosphere%2C%20high%20quality%20photography%2C%20rich%20textures&width=1920&height=600&seq=cta-bg&orientation=landscape"
          alt="开始在线麻将牌游戏"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 max-w-3xl mx-auto text-center px-4">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            准备好开始你的<strong className="text-accent-400">在线麻将牌</strong>之旅了吗？
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            数百万玩家已在在线麻将牌找到了乐趣，现在加入我们，免费畅玩正宗麻将游戏！
          </p>
          <Link
            to="/category/guobiao"
            className="inline-flex px-8 py-3 bg-accent-500 text-background-50 rounded-full text-base font-semibold hover:bg-accent-600 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            免费开始游戏
          </Link>
        </div>
      </section>
    </>
  );
}